import { ALLOWED_CHARS_MODE_PRESETS } from './content.js';
import { classifyField } from './field-classifier.js';
import { escapeRegex } from './regex-assist.js';
/** Stages where an operator keys/reviews values, so derived field-intrinsic
 *  validation applies. Excludes import/ocr/export stages where there is no
 *  keying surface to trap. Single source for both the VS bundle builder
 *  (`collectValidateBindings`) and the Rules-tab derived group. */
const DERIVED_VALIDATION_STAGES = new Set([
    'key-entry',
    'verification',
    'qa',
    'supervisor',
    'batch-fix',
]);
/** True when derived validation rules should surface/enforce at this stage. */
export function isDerivedValidationStage(stage) {
    return DERIVED_VALIDATION_STAGES.has(stage);
}
/** Human label per source — single home so the Rules-tab group and the field
 *  drawer readout stay consistent. */
const SOURCE_LABELS = {
    isRequired: 'Required',
    regexPattern: 'Pattern',
    inputMask: 'Input mask',
    // The `dataType` source always passes a specific label ('Number format' /
    // 'Date format') at the call site; this neutral default is never rendered.
    dataType: 'Format',
    minLength: 'Min length',
    maxLength: 'Max length',
    minValue: 'Min value',
    maxValue: 'Max value',
    allowedChars: 'Allowed characters',
};
/**
 * Mask → anchored regex. The mask token convention (see field-editor
 * `DisplaySection`): `9` = digit, `A` = letter, `*` = alphanumeric (any).
 * Every other character is a literal and is regex-escaped. Consecutive
 * identical class tokens collapse to `{n}` for a compact pattern.
 */
export function maskToRegexPattern(mask) {
    const tokenClass = {
        '9': '\\d',
        A: '[A-Za-z]',
        '*': '[A-Za-z0-9]',
    };
    let body = '';
    let i = 0;
    while (i < mask.length) {
        const ch = mask[i];
        const cls = tokenClass[ch];
        if (cls) {
            // Collapse a run of the same class token into `{n}`.
            let count = 1;
            while (i + count < mask.length && mask[i + count] === ch)
                count++;
            body += count === 1 ? cls : `${cls}{${count}}`;
            i += count;
        }
        else {
            body += escapeRegex(ch);
            i += 1;
        }
    }
    return `^${body}$`;
}
/**
 * F-026 — a keyer-friendly default message for a dataType format failure.
 * Mirrors the patterns in `dataTypePattern`; returns undefined for types with
 * no intrinsic format (those never emit a dataType pattern rule).
 */
function dataTypeFormatMessage(dataType) {
    switch (dataType) {
        case 'number':
            return 'Enter a whole number';
        case 'decimal':
            return 'Enter a number (decimals allowed)';
        case 'currency':
            return 'Enter an amount (e.g. 1,234.56)';
        case 'date':
            return 'Enter a date as MM/DD/YYYY or YYYY-MM-DD';
        default:
            return undefined;
    }
}
/** F-026 — a keyer-friendly default message for an allowedChars failure. */
function allowedCharsMessage(mode) {
    switch (mode) {
        case 'letters':
            return 'Use letters only';
        case 'alphanumeric':
            return 'Use letters and numbers only';
        case 'digits':
            return 'Use digits only';
        default:
            return undefined;
    }
}
/**
 * dataType → format pattern. Only the value-typed dataTypes get a format
 * check; text/boolean/lookup/dropdown carry no intrinsic format.
 * Returns `null` when the type has no derived format.
 */
function dataTypePattern(dataType) {
    switch (dataType) {
        case 'number':
            // Optional sign, integer only.
            return '^-?\\d+$';
        case 'decimal':
            // Optional sign, integer with optional fractional part.
            return '^-?\\d+(\\.\\d+)?$';
        case 'currency':
            // Optional symbol, optional sign, grouped or plain integer, optional cents.
            return '^[^\\d-]?-?\\d{1,3}(,?\\d{3})*(\\.\\d+)?$';
        case 'date':
            // ISO (YYYY-MM-DD) or US slash (MM/DD/YYYY) — the two formats the field
            // editor's dateFormat presets cover.
            return '^(\\d{4}-\\d{2}-\\d{2}|\\d{1,2}/\\d{1,2}/\\d{4})$';
        default:
            return null;
    }
}
/** allowedCharsMode → char-class pattern over the whole value. Mirrors the
 *  presets the field editor writes into `regexPattern` (see content.ts).
 *  `any` → no rule; `custom` → defers to `regexPattern` (no separate rule). */
function allowedCharsPattern(mode) {
    // 'any' (clears) and 'custom' (uses regexPattern) → no separate rule; the
    // three real presets come from the shared single-source table.
    return mode === 'letters' || mode === 'alphanumeric' || mode === 'digits'
        ? ALLOWED_CHARS_MODE_PRESETS[mode]
        : null;
}
/**
 * Derive the canonical validation rules for a single field from its settings.
 * Pure. Returns `[]` for fields that get no auto-validation per the design:
 *   - computed / calculated fields (derived values, not keyed)
 *   - fields whose review disposition is `skip` (auto-accept, never keyed)
 *
 * Pattern precedence (avoid emitting conflicting patterns for one field):
 *   regexPattern  >  inputMask  >  allowedCharsMode/dataType-format
 * The most specific authored intent wins; lower tiers are suppressed so the VS
 * never traps on two competing format checks.
 */
export function deriveFieldValidations(field) {
    // Exceptions: computed/static fields and skip-policy fields are not keyed,
    // so they get no auto-validation (design §3/§6 "Exceptions").
    if (field.verificationPolicy === 'skip')
        return [];
    const kind = classifyField(field);
    if (kind === 'computed' || kind === 'static')
        return [];
    const overrides = field.derivedValidationOverrides ?? {};
    const out = [];
    const apply = (source, rule, labelOverride, 
    // F-026 — a generated, format-specific default message for keyers (e.g.
    // "Enter a date as MM/DD/YYYY"). Used only for pattern rules, where the
    // engine's generic "does not match the required format" is unhelpful. An
    // authored override still wins over this default.
    defaultMessage) => {
        const o = overrides[source];
        if (o?.disabled)
            return;
        // E-024 — authored message REPLACES the generated one; ignore blank/whitespace.
        const authored = typeof o?.message === 'string' && o.message.trim() ? o.message : undefined;
        const message = authored ?? defaultMessage;
        // E-024 — cosmetic label; documentation only, ignore blank/whitespace.
        const label = typeof o?.label === 'string' && o.label.trim() ? o.label : undefined;
        out.push({
            source,
            sourceLabel: labelOverride ?? SOURCE_LABELS[source],
            rule,
            severity: o?.severity ?? 'error',
            ...(o?.overrideBehavior !== undefined ? { overrideBehavior: o.overrideBehavior } : {}),
            ...(message ? { message } : {}),
            ...(label ? { label } : {}),
        });
    };
    // ── required ──
    if (field.isRequired === true) {
        apply('isRequired', { type: 'required' });
    }
    // ── pattern (single winner across regexPattern / inputMask / charClass) ──
    // Precedence: authored regexPattern beats a mask-derived pattern beats an
    // allowedChars char-class beats a dataType format. Only one pattern is
    // emitted per field. Lower tiers are computed lazily so the winning case
    // never pays for the others (notably the mask scan).
    const regexPattern = field.regexPattern?.trim();
    const trimmedMask = field.inputMask?.trim();
    if (regexPattern) {
        // A raw regex can't be humanized; a short actionable hint still beats the
        // engine's generic "does not match the required format".
        apply('regexPattern', { type: 'pattern', pattern: regexPattern }, undefined, 'Match the required pattern');
    }
    else if (trimmedMask) {
        apply('inputMask', { type: 'pattern', pattern: maskToRegexPattern(trimmedMask) }, undefined, `Match the format ${trimmedMask}`);
    }
    else {
        const charClass = field.allowedCharsMode ? allowedCharsPattern(field.allowedCharsMode) : null;
        if (charClass) {
            apply('allowedChars', { type: 'pattern', pattern: charClass }, undefined, field.allowedCharsMode ? allowedCharsMessage(field.allowedCharsMode) : undefined);
        }
        else {
            const formatPattern = dataTypePattern(field.dataType);
            if (formatPattern) {
                apply('dataType', { type: 'pattern', pattern: formatPattern }, field.dataType === 'date' ? 'Date format' : 'Number format', dataTypeFormatMessage(field.dataType));
            }
        }
    }
    // ── length (ONE rule carrying only the set bounds) ──
    // Keyed downstream (`content-validate` ruleKey) by the bare type "length",
    // NOT by its bounds — unlike `compare` (single-op, so keyed by op). Only one
    // derived length per field exists, so an authored `length` of any bounds
    // correctly dedups it (authored wins). Keying by bounds would break that.
    const hasMinLen = typeof field.minLength === 'number';
    const hasMaxLen = typeof field.maxLength === 'number';
    if (hasMinLen || hasMaxLen) {
        // Source key follows the lower bound when present (its override governs the
        // combined rule); falls back to maxLength when only the upper bound is set.
        const lengthSource = hasMinLen ? 'minLength' : 'maxLength';
        apply(lengthSource, {
            type: 'length',
            ...(hasMinLen ? { min: field.minLength } : {}),
            ...(hasMaxLen ? { max: field.maxLength } : {}),
        });
    }
    // ── compare (one per numeric bound; engine compare is single-op) ──
    if (typeof field.minValue === 'number') {
        apply('minValue', { type: 'compare', op: 'gte', value: field.minValue });
    }
    if (typeof field.maxValue === 'number') {
        apply('maxValue', { type: 'compare', op: 'lte', value: field.maxValue });
    }
    return out;
}
//# sourceMappingURL=derived-validation.js.map